import React, { useEffect, useState } from "react";
import { Truck, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";

export const TRANSPORTE_VACIO = { chofer_id: "", nombre_chofer: "", rut_chofer: "", patente: "", rut_transportista: "" };
export const transportePayload = (t) =>
  t && (t.nombre_chofer || t.rut_chofer || t.patente || t.rut_transportista) ? t : undefined;
const rutOk = (rut) => {
  const r = String(rut || "").replace(/[^0-9kK]/g, "").toUpperCase();
  if (r.length < 2 || !/^\d+$/.test(r.slice(0, -1))) return false;
  let s = 0, m = 2;
  for (const d of r.slice(0, -1).split("").reverse()) { s += Number(d) * m; m = m === 7 ? 2 : m + 1; }
  const dv = 11 - (s % 11);
  return r.slice(-1) === (dv === 11 ? "0" : dv === 10 ? "K" : String(dv));
};
// Mismas reglas que el servidor: se revisa antes de cobrar o despachar
export const validarTransporte = (t) => {
  const v = transportePayload(t);
  if (!v) return null;
  if (!!String(v.nombre_chofer || "").trim() !== !!String(v.rut_chofer || "").trim()) return "Transporte: indica nombre y RUT del chofer (o deja ambos vacíos)";
  if (v.rut_chofer && !rutOk(v.rut_chofer)) return "Transporte: el RUT del chofer no es válido";
  if (v.rut_transportista && !rutOk(v.rut_transportista)) return "Transporte: el RUT del transportista no es válido";
  const pat = String(v.patente || "").replace(/[^A-Za-z0-9]/g, "");
  if (pat && pat.length < 4) return "Transporte: la patente debe tener al menos 4 caracteres";
  return null;
};

// Datos opcionales de transporte (Res. SII): chofer, RUT del transportista y patente del vehículo
// storeId: sucursal donde se emite ("auto" = la del usuario o la casa matriz) para prellenar su chofer por defecto
export default function TransporteFields({ value, onChange, prefix = "transporte", compact = false, storeId }) {
  const { user } = useAuth() || {};
  const [abierto, setAbierto] = useState(!!transportePayload(value));
  const [choferes, setChoferes] = useState([]);
  const [tienda, setTienda] = useState(storeId === "auto" ? user?.store_id : storeId);
  useEffect(() => { api.get("/choferes").then((r) => setChoferes((r.data || []).filter((c) => c.activo !== false))).catch(() => {}); }, []);
  useEffect(() => {
    if (storeId !== "auto") { setTienda(storeId); return; }
    if (user?.store_id) { setTienda(user.store_id); return; }
    api.get("/stores").then((r) => setTienda(((r.data || []).find((s) => s.es_casa_matriz) || (r.data || [])[0])?.store_id)).catch(() => {});
  }, [storeId, user?.store_id]);
  const v = value || TRANSPORTE_VACIO;
  const defecto = tienda && choferes.find((c) => (c.sucursales_defecto || []).includes(tienda));
  // Documento nuevo = TRANSPORTE_VACIO (misma referencia). Si el usuario lo quitó o lo borró a mano se usa
  // una copia, así el chofer por defecto no vuelve a aparecer al reabrir; tras emitir se vuelve a prellenar.
  const esNuevo = value === TRANSPORTE_VACIO || value == null;
  useEffect(() => {
    if (defecto && esNuevo) { elegir(defecto.chofer_id); setAbierto(true); }
  }, [defecto?.chofer_id, esNuevo]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => onChange({ ...v, [k]: k === "patente" ? e.target.value.toUpperCase() : e.target.value });
  const elegir = (id) => {
    const c = choferes.find((x) => x.chofer_id === id);
    onChange(c ? { chofer_id: c.chofer_id, nombre_chofer: c.nombre, rut_chofer: c.rut, patente: c.patente || "",
                   rut_transportista: c.rut_transportista || "" } : { ...TRANSPORTE_VACIO });
  };
  return (
    <div className="rounded-2xl border border-slate-200" data-testid={`${prefix}-section`}>
      <button type="button" onClick={() => setAbierto(!abierto)} data-testid={`${prefix}-toggle`}
        className="w-full flex items-center gap-2 px-4 py-3 text-left">
        <Truck className="w-4 h-4 text-sky-600" />
        <span className="flex-1 text-sm font-semibold text-slate-800">
          Transporte <span className="font-normal text-slate-500">(opcional · chofer y patente del vehículo)</span>
        </span>
        {transportePayload(v) && !abierto && <span className="text-xs text-emerald-600">{v.nombre_chofer || ""} {v.patente}</span>}
        {transportePayload(v) && abierto && (
          <span role="button" tabIndex={0} data-testid={`${prefix}-quitar`} onClick={(e) => { e.stopPropagation(); onChange({ ...TRANSPORTE_VACIO }); }}
            className="text-xs font-semibold text-rose-600 hover:underline">Quitar transporte</span>
        )}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${abierto ? "rotate-180" : ""}`} />
      </button>
      {abierto && (
        <div className={`px-4 pb-4 grid gap-3 ${compact ? "grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-4"}`}>
          <label className={`text-xs text-slate-600 ${compact ? "col-span-2" : "sm:col-span-2 lg:col-span-4"}`}>
            Chofer guardado
            <select value={v.chofer_id || ""} onChange={(e) => elegir(e.target.value)} data-testid={`${prefix}-chofer`}
              className="mt-1 h-10 w-full rounded-xl border border-slate-200 px-3 text-sm bg-white">
              <option value="">— Ingresar a mano —</option>
              {choferes.map((c) => <option key={c.chofer_id} value={c.chofer_id}>{c.nombre} · {c.rut}{c.patente ? ` · ${c.patente}` : ""}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-600">Nombre del chofer
            <Input value={v.nombre_chofer} onChange={set("nombre_chofer")} maxLength={30} data-testid={`${prefix}-nombre`} className="mt-1 h-10 rounded-xl" /></label>
          <label className="text-xs text-slate-600">RUT del chofer
            <Input value={v.rut_chofer} onChange={set("rut_chofer")} placeholder="12.345.678-9" data-testid={`${prefix}-rut-chofer`} className="mt-1 h-10 rounded-xl" /></label>
          <label className="text-xs text-slate-600">RUT del transportista
            <Input value={v.rut_transportista} onChange={set("rut_transportista")} placeholder="Vacío: RUT de la empresa" data-testid={`${prefix}-rut-transportista`} className="mt-1 h-10 rounded-xl" /></label>
          <label className="text-xs text-slate-600">Patente vehículo / carro
            <Input value={v.patente} onChange={set("patente")} maxLength={8} placeholder="ABCD12" data-testid={`${prefix}-patente`} className="mt-1 h-10 rounded-xl font-mono uppercase" /></label>
        </div>
      )}
    </div>
  );
}
